"use client";

import { useState, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";

import { useStyles } from "./styles";
import { MODELS } from "../constants";
import ModelButton from "./ModelButton";
import AutoModelIcon from "@/assets/models/auto-model-icon.webp";
import IdyllicBaseIcon from "@/assets/models/idyllic-base-icon.webp";
import IdyllicHDIcon from "@/assets/models/idyllic-hd-icon.webp";
import IdyllicHDRIcon from "@/assets/models/idyllic-hdr-plus-icon.webp";
import NSFW_FastIcon from "@/assets/models/nsfw-fast-icon.webp";
import Imagen4Icon from "@/assets/models/imagen-4-icon.webp";
import NSFW_Icon from "@/assets/models/nsfw-icon.webp";
import GPT_Icon from "@/assets/models/gpt-icon.webp";
import FluxProIcon from "@/assets/models/flux-pro-icon.webp";
import Imagen4UltraIcon from "@/assets/models/imagen-4-ultra-icon.webp";
import Grok2Icon from "@/assets/models/grok-2-icon.webp";

interface ModelSelectProps {
  selectedModes: {
    isPublic: boolean;
    is18Plus: boolean;
    isTextImage: boolean;
  };
  aspectRatio: string;
  hasImages: boolean;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  isSubscribed: boolean;
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  selectedModes,
  aspectRatio,
  hasImages,
  selectedModel,
  setSelectedModel,
  isSubscribed,
}) => {
  const { classes, cx } = useStyles();
  const [showAllModels, setShowAllModels] = useState(false);

  const toggleShowAllModels = () => {
    setShowAllModels((prev) => !prev);
  };

  const isModelDisabled = useCallback(
    (selected: string) => {
      const model = MODELS.find((m) => m.name === selected);
      if (!model) return false;

      if (!model.nsfw && selectedModes.is18Plus) return true;
      if (!model.imageText && selectedModes.isTextImage) return true;
      if (!model.includedAspectRatios.includes(aspectRatio)) return true;
      if (!model.imageEdits && hasImages) return true;

      return false;
    },
    [selectedModes, aspectRatio, hasImages]
  );

  const isModelLocked = (modelName: string) => {
    if (isSubscribed) return false;

    const model = MODELS.find((m) => m.name === modelName);
    return model ? model.premiumOnly : false;
  };

  useEffect(() => {
    if (isModelDisabled(selectedModel)) {
      setSelectedModel("auto");
    }
  }, [selectedModel, selectedModes]);

  return (
    <div className={classes.container}>
      <h3 className={classes.heading}>Model</h3>
      <div className={classes.controls}>
        <div className={classes.mainControls}>
          <ModelButton
            value="auto"
            label="Auto"
            imageSrc={AutoModelIcon.src}
            selectedModel={selectedModel}
            onClick={() => setSelectedModel("auto")}
            locked={isModelLocked("auto")}
          />
          <ModelButton
            value="hd-turbo"
            label="Idyllic Base"
            imageSrc={IdyllicBaseIcon.src}
            selectedModel={selectedModel}
            onClick={() => setSelectedModel("hd-turbo")}
            locked={isModelLocked("hd-turbo")}
          />
          <ModelButton
            value="hd"
            label="Idyllic HD"
            imageSrc={IdyllicHDIcon.src}
            selectedModel={selectedModel}
            onClick={() => setSelectedModel("hd")}
            locked={isModelLocked("hd")}
          />
          <ModelButton
            value="imagen-4"
            label="Imagen 4"
            imageSrc={Imagen4Icon.src}
            selectedModel={selectedModel}
            disabled={isModelDisabled("imagen-4")}
            onClick={() => setSelectedModel("imagen-4")}
            locked={isModelLocked("imagen-4")}
          />
          <ModelButton
            value="gpt"
            label="GPT Image"
            imageSrc={GPT_Icon.src}
            selectedModel={selectedModel}
            disabled={isModelDisabled("gpt")}
            onClick={() => setSelectedModel("gpt")}
            locked={isModelLocked("gpt")}
          />
          <ModelButton
            value="imagen-4-ultra"
            label="Imagen 4 Ultra"
            imageSrc={Imagen4UltraIcon.src}
            selectedModel={selectedModel}
            disabled={isModelDisabled("imagen-4-ultra")}
            onClick={() => setSelectedModel("imagen-4-ultra")}
            locked={isModelLocked("imagen-4-ultra")}
          />
          {!showAllModels && (
            <div
              className={classes.moreModelsButtonWrapper}
              onClick={toggleShowAllModels}
            >
              <div className={classes.moreModelsButton}>+5 More Models</div>
            </div>
          )}
        </div>
        {showAllModels && (
          <div>
            <div className={classes.additionalModels}>
              <ModelButton
                value="flux-pro"
                label="Flux Pro"
                imageSrc={FluxProIcon.src}
                selectedModel={selectedModel}
                disabled={isModelDisabled("flux-pro")}
                onClick={() => setSelectedModel("flux-pro")}
                locked={isModelLocked("flux-pro")}
              />
              <ModelButton
                value="nsfw-fast"
                label="NSFW Fast"
                imageSrc={NSFW_FastIcon.src}
                selectedModel={selectedModel}
                disabled={isModelDisabled("nsfw-fast")}
                onClick={() => setSelectedModel("nsfw-fast")}
                locked={isModelLocked("nsfw-fast")}
              />
              <ModelButton
                value="nsfw"
                label="NSFW"
                imageSrc={NSFW_Icon.src}
                selectedModel={selectedModel}
                disabled={isModelDisabled("nsfw")}
                onClick={() => setSelectedModel("nsfw")}
                locked={isModelLocked("nsfw")}
              />
              <ModelButton
                value="hd+"
                label="Idyllic HDR+"
                imageSrc={IdyllicHDRIcon.src}
                selectedModel={selectedModel}
                disabled={isModelDisabled("hd+")}
                onClick={() => setSelectedModel("hd+")}
                locked={isModelLocked("hd+")}
              />
              <ModelButton
                value="grok-2"
                label="Grok 2"
                imageSrc={Grok2Icon.src}
                selectedModel={selectedModel}
                disabled={isModelDisabled("grok-2")}
                onClick={() => setSelectedModel("grok-2")}
                locked={isModelLocked("grok-2")}
              />
            </div>
          </div>
        )}
      </div>
      <button
        onClick={toggleShowAllModels}
        className={cx(classes.toggleButton, {
          [classes.toggleButtonExpanded]: showAllModels,
        })}
      >
        <span>Show {showAllModels ? "Less" : "More"} Models</span>
        <ChevronDown size={16} />
      </button>
    </div>
  );
};

export default ModelSelect;
